/*******************************************************************************
 * Universal Pixel Router  —  v1.0
 * =============================================================================
 * The storefront half of the pixel. Two jobs:
 *
 *   1. Publish the store's authoritative market/localization context to the
 *      sandboxed pixel, so market_key is correct BEFORE the first ecommerce
 *      event and independent of URL guessing.
 *   2. Bridge postMessage events from the sandboxed pixel into the page
 *      dataLayer, so the storefront GTM container and GTM Preview both see them.
 *
 * Runs on the STOREFRONT only — the checkout sandbox loads its own GTM.
 *
 * -----------------------------------------------------------------------------
 * INSTALL (either works)
 *
 * A) Asset + Liquid config (recommended — keeps JS out of theme.liquid)
 *    1. Save this file as `assets/pixel-router.js`. Any name works, but it must
 *       match the reference in blubolt_theme-snippet.liquid EXACTLY — Shopify
 *       serves a 404 for a mismatch and the bridge silently never installs.
 *    2. Add to `theme.liquid`, just before </head>:
 *
 *         <script>
 *           window.pixelRouterConfig = {
 *             debug: false,
 *             userId: {% if customer %}{{ customer.id | json }}{% else %}null{% endif %},
 *             market: {
 *               market_handle: {{ localization.market.handle | json }},
 *               market_id:     {{ localization.market.id | json }},
 *               country_code:  {{ localization.country.iso_code | json }},
 *               language_code: {{ localization.language.iso_code | json }},
 *               currency_code: {{ localization.country.currency.iso_code | json }}
 *             }
 *           };
 *         </script>
 *         <script src="{{ 'pixel-router.js' | asset_url }}" defer></script>
 *
 * B) Inline
 *    Paste the Liquid config block above, then this whole file wrapped in
 *    <script> tags, into theme.liquid before </head>.
 *
 * Both blocks must load BEFORE the GTM container snippet.
 *
 * Single-market stores can omit `market` entirely — the router still bridges
 * events and the pixel runs in single-property mode.
 *
 * Requires the Web Pixel to have the custom-event permission enabled, otherwise
 * `Shopify.analytics.publish` is a no-op and the pixel falls back to URL/geo
 * inference (still correct on the storefront, less reliable in checkout).
 ******************************************************************************/

(function () {
  'use strict';

  var cfg   = (window.pixelRouterConfig || {});
  var DEBUG = cfg.debug === true;

  /* Set false to only accept messages from your own origin. Shopify pixel
     sandboxes post from a rotating origin, so true is required in practice. */
  var ACCEPT_ANY_ORIGIN = cfg.acceptAnyOrigin !== false;
  var SAFE_ORIGINS      = cfg.safeOrigins || [location.origin];

  function log(){ if (DEBUG) { try { console.log.apply(console, arguments); } catch(e){} } }
  function warn(){ if (DEBUG) { try { console.warn.apply(console, arguments); } catch(e){} } }

  /* Harmless if GTM has already created them. */
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function(){ window.dataLayer.push(arguments); };

  if (window.__pixelRouterActive) { log('[pixel-router] already active'); return; }
  window.__pixelRouterActive = true;

  /* ---- 1. Customer id for GA4 user_id ----------------------------------- */
  if (cfg.userId != null && cfg.userId !== '') {
    window.dataLayer.push({ user_id: String(cfg.userId) });
    log('[pixel-router] user_id published');
  }

  /* ---- 2. Publish market context to the pixel ---------------------------
   * Liquid knows the selected market with certainty. The pixel treats this as
   * higher priority than URL inference, and it covers the cases URL matching
   * cannot: several locales sharing one market, markets on the same path, or a
   * primary market with no URL prefix at all.
   * -------------------------------------------------------------------- */
  if (cfg.market && typeof cfg.market === 'object') {
    try {
      if (window.Shopify && window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('analytics:market_context', cfg.market);
        log('[pixel-router] market context published', cfg.market);
      } else {
        warn('[pixel-router] Shopify.analytics.publish unavailable');
      }
    } catch (e) { warn('[pixel-router] market context publish failed', e); }
  }

  /* ---- 3. Bridge sandbox postMessage -> dataLayer ------------------------ */
  function isAllowedOrigin(origin) {
    if (ACCEPT_ANY_ORIGIN) return true;
    try { return SAFE_ORIGINS.indexOf(origin) > -1; } catch(e){ return false; }
  }

  function isPixelMessage(d) {
    return !!(d && d.__fromShopifyPixel === true && (('clear' in d) || ('event' in d)));
  }

  /* Drop an identical payload repeated within 100ms (double-bound listeners,
     Turbolinks-style re-renders). */
  var lastSignature = null, lastAt = 0;
  function signature(obj){
    try { return JSON.stringify(obj); } catch(e){ return String(Math.random()); }
  }
  function isDuplicate(payload){
    var now = Date.now(), sig = signature(payload);
    if (lastSignature === sig && (now - lastAt) < 100) return true;
    lastSignature = sig; lastAt = now;
    return false;
  }

  function handleMessage(evt) {
    var d = evt && evt.data;
    if (!isPixelMessage(d)) return;
    if (!isAllowedOrigin(evt.origin)) return;

    try {
      if (d.clear === true) {
        window.dataLayer.push({ ecommerce: null });
        log('[pixel-router] ecommerce cleared');
        return;
      }

      if (!d.event) return;

      var out = { event: d.event };
      if (d.ecommerce && typeof d.ecommerce === 'object') out.ecommerce = d.ecommerce;

      /* Forward every extra field verbatim: market_key, ga4_market_id,
         page_location, tracking_context, the GA4 session bridge, etc. */
      if (d.extra && typeof d.extra === 'object') {
        for (var k in d.extra) {
          if (Object.prototype.hasOwnProperty.call(d.extra, k)) out[k] = d.extra[k];
        }
      }

      if (isDuplicate(out)) { log('[pixel-router] deduped', out.event); return; }

      window.dataLayer.push(out);
      log('[pixel-router] -> dataLayer', out);
    } catch (err) {
      warn('[pixel-router] routing error', err);
    }
  }

  window.addEventListener('message', handleMessage, false);

  /* ---- 4. Drain anything the early buffer caught -------------------------
   * This file is loaded with `defer`, so it executes after the document is
   * parsed — but the pixel sandbox can boot and fire `page_viewed` before that.
   * postMessage is not buffered by the browser: with no listener attached, the
   * message is simply gone, and the first pageview of the session is lost. It
   * is a race, so it fails intermittently, which is worse than failing outright.
   *
   * blubolt_theme-snippet.liquid installs a tiny inline listener that queues
   * pixel messages from the moment <head> is parsed. We replay them here, in
   * order, then retire it.
   * -------------------------------------------------------------------- */
  try {
    if (typeof window.__pixelRouterEarly === 'function') {
      window.removeEventListener('message', window.__pixelRouterEarly, false);
      window.__pixelRouterEarly = null;
    }
    var queued = window.__pixelRouterQueue || [];
    window.__pixelRouterQueue = [];
    if (queued.length) log('[pixel-router] replaying', queued.length, 'buffered message(s)');
    for (var q = 0; q < queued.length; q++) handleMessage(queued[q]);
  } catch (e) { warn('[pixel-router] buffer drain failed', e); }

  log('[pixel-router] installed');
})();
